import Image from 'next/image';
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { getClassByCode, htmlToText } from '../../shared/useUtils';
import {
  IAppOrder,
  IAppOrderClass,
  IAppOrderCountry,
  IAppOrderSubClass,
} from '../../store/models/store.models';
import { ModalStyle } from '../../styles/components/common/ui';
import { OrderProcessDetailsStyle } from '../../styles/components/order-process';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '../common/ui';
import DropDownFilterable, {
  IDropDownFilterableOption,
} from '../common/ui/DropDownFilterable';
import Message from '../common/ui/Message';

export interface IOrderProcessDetailsHandle {
  validateStep: () => boolean;
}

interface IOrderProcessDetailsProps {
  countries: IAppOrderCountry[];
  classes: IAppOrderClass[];
  labels: {
    countrySelectionTitle: string;
    countrySelectionPlaceholder: string;
    countrySelectionAddButton: string;
    trademarkInformationTitle: string;
    trademarkInformationPlaceholder: string;
    uploadLogoTip: string;
    goodsAndServicesTitle: string;
    goodsAndServicesDescription: string;
    goodsAndServicesNotice: string;
    goodsAndServicesButtonLabel: string;
    goodsAndServicesButtonTip: string;
    countryRemoveButton: string;
    uploadLogoTitle: string;
    uploadLogoRemoveButton: string;
    goodsAndServicesSelectedTitle: string;
    goodsAndServicesRemoveButton: string;
    goodsAndServicesModalTitle: string;
    goodsAndServicesModalDescription: string;
    goodsAndServicesModalCancelButton: string;
    goodsAndServicesModalDoneButton: string;
    goodsAndServicesClassDescripionPlaceholder: string;
    goodsAndServicesSearchPlaceholder: string;
    formValidation: {
      requiredField: string;
    };
    showMore: string;
    showLess: string;
    noServiceAvailableMessage?: string;
  };
  order: IAppOrder;
  orderChange: (order: IAppOrder) => void;
  showNoServiceAvailableMessage?: boolean;
}

interface IComponentState {
  classesTextSearchTerm: string;
  showClassesModal: boolean;
  checkedClasses: IAppOrderClass[];
  errors: {
    trademarkName: string | undefined;
    trademarkCountries: string | undefined;
  };
}

const OrderProcessDetails = forwardRef(
  (props: IOrderProcessDetailsProps, ref: any) => {
    const initialComponentState: IComponentState = {
      classesTextSearchTerm: '',
      showClassesModal: false,
      checkedClasses: [],
      errors: {
        trademarkName: undefined,
        trademarkCountries: undefined,
      },
    };
    const [state, setState] = useState(initialComponentState);
    const [order, setOrder] = useState<IAppOrder>();
    const [classes, setClasses] = useState<IAppOrderClass[]>();
    const countryOptions: IDropDownFilterableOption[] = useMemo(() => {
      return props.countries.map((x) => ({
        label: x.name,
        value: x.id,
      }));
    }, [props.countries]);

    const addCountry = (countryId: number) => {
      const currentOrder = { ...order } as IAppOrder;
      const country = props.countries.find((x) => x.id === countryId);

      if (
        country &&
        !currentOrder.trademarkCountries.find((x) => x.id === countryId)
      ) {
        currentOrder.trademarkCountries = [
          ...currentOrder.trademarkCountries,
          country,
        ];
        setOrder(currentOrder);
        props.orderChange({ ...currentOrder });
      }

      validateTrademarkCountries(currentOrder.trademarkCountries);
    };

    const removeCountry = (countryId: number) => {
      const currentOrder = { ...order } as IAppOrder;
      currentOrder.trademarkCountries = currentOrder.trademarkCountries.filter(
        (x) => x.id !== countryId
      );
      setOrder(currentOrder);
      props.orderChange({ ...currentOrder });
    };

    const setTrademarkName = (event: React.ChangeEvent<HTMLInputElement>) => {
      const currentOrder = { ...order } as IAppOrder;
      currentOrder.trademarkName = event.target.value;
      setOrder(currentOrder);
      props.orderChange({ ...currentOrder });
      validateTrademarkName(currentOrder.trademarkName);
    };

    const setTrademarkLogo = (event: React.ChangeEvent<HTMLInputElement>) => {
      const currentOrder = { ...order } as IAppOrder;
      if (event.target.files) {
        const file = event.target.files[0];
        if (file) {
          const fileReader = new FileReader();
          fileReader.onloadend = (e) => {
            currentOrder.trademarkLogo = (e.target as any).result;
            setOrder(currentOrder);
            props.orderChange({ ...currentOrder });
          };
          fileReader.readAsDataURL(file);
        } else {
          resetTrademarkLogo();
        }
      }
    };

    const setClassDescription = (
      value: string,
      id?: number,
      fromModal?: boolean
    ) => {
      if (!id) return;
      const currentState = { ...state };
      const checkedClass = currentState.checkedClasses.find((x) => x.id === id);

      if (!!checkedClass) {
        const trademarkClass = { ...checkedClass };
        trademarkClass.description = value;
        currentState.checkedClasses = [...currentState.checkedClasses].filter(
          (x) => x.id !== id
        );
        currentState.checkedClasses = [
          ...currentState.checkedClasses,
          { ...(trademarkClass as IAppOrderClass) },
        ];
        setState(currentState);

        if (!fromModal) {
          const currentOrder = { ...order } as IAppOrder;
          const trademarkClass = {
            ...currentOrder.trademarkClasses.find((x) => x.id === id),
          };

          if (trademarkClass) {
            trademarkClass.description = value;
            currentOrder.trademarkClasses =
              currentOrder.trademarkClasses.filter((x) => x.id !== id);
            currentOrder.trademarkClasses = [
              ...currentOrder.trademarkClasses,
              { ...(trademarkClass as IAppOrderClass) },
            ];
            setOrder(currentOrder);
            props.orderChange({ ...currentOrder });
          }
        }
      }
    };

    const resetTrademarkLogo = () => {
      const currentOrder = { ...order } as IAppOrder;
      currentOrder.trademarkLogo = '';
      setOrder(currentOrder);
      props.orderChange({ ...currentOrder });
    };

    const filteredClasses = () => {
      let filtered = [];

      if (
        state.classesTextSearchTerm &&
        state.classesTextSearchTerm.length >= 2
      ) {
        filtered = props.classes.filter((x) => {
          return (
            x.name
              .toLowerCase()
              .includes(state.classesTextSearchTerm.toLowerCase()) ||
            x.subClasses?.some((y: IAppOrderSubClass) =>
              y.description?.text
                ?.toLowerCase()
                .includes(state.classesTextSearchTerm.toLowerCase())
            )
          );
        });
      } else {
        filtered = props.classes;
      }

      filtered.forEach((x) => {
        const checkedClass = state.checkedClasses.find(
          (c) => c.id === x.id
        ) as IAppOrderClass;
        x.description = !!checkedClass ? checkedClass.description : '';
      });

      return filtered;
    };

    const handleTextSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
      const currentState = { ...state };
      currentState.classesTextSearchTerm = event.target.value;
      setState(currentState);
    };

    const handleShowClassesModal = () => {
      const currentOrder = { ...order } as IAppOrder;
      const currentState = { ...state };

      if (currentOrder.trademarkClasses.length > 0) {
        currentState.checkedClasses = [...currentOrder.trademarkClasses];
      }

      currentState.showClassesModal = true;
      setState(currentState);
    };

    const handleDismissClassesModal = () => {
      const currentState = { ...state };
      currentState.classesTextSearchTerm = '';
      currentState.showClassesModal = false;
      setState(currentState);
    };

    const handleDoneClassesModal = () => {
      const currentOrder = { ...order } as IAppOrder;
      currentOrder.trademarkClasses = [...state.checkedClasses];
      setOrder(currentOrder);
      props.orderChange({ ...currentOrder });
      handleDismissClassesModal();
    };

    const handleClassCheckedChange = (
      event: ChangeEvent<HTMLInputElement>,
      classItem: { id: number; name: string; code: number }
    ) => {
      const currentState = { ...state };

      if (event.target.checked) {
        currentState.checkedClasses = [
          ...currentState.checkedClasses,
          { ...classItem, description: '' },
        ];
      } else {
        currentState.checkedClasses = currentState.checkedClasses.filter(
          (x) => x.id !== classItem.id
        );
      }

      setState(currentState);
    };

    const handleClassRemove = (classItem?: {
      id: number;
      name: string;
      code: number;
    }) => {
      if (!classItem) return;
      const currentOrder = { ...order } as IAppOrder;
      const currentState = { ...state };
      currentState.checkedClasses = currentState.checkedClasses?.filter(
        (x) => x.id !== classItem.id
      );
      currentOrder.trademarkClasses = currentOrder.trademarkClasses?.filter(
        (x) => x.id !== classItem.id
      );
      setOrder(currentOrder);
      setState(currentState);
      props.orderChange({ ...currentOrder });
    };

    const isClassChecked = (item: IAppOrderClass) => {
      return state.checkedClasses.find((x) => x.id === item.id) !== undefined;
    };

    const validateTrademarkCountries = (
      trademarkCountries?: IAppOrderCountry[]
    ) => {
      const currentState = { ...state };

      if (!trademarkCountries) {
        trademarkCountries = props.order!.trademarkCountries;
      }

      if (trademarkCountries.length > 0) {
        currentState.errors.trademarkCountries = undefined;
      } else {
        currentState.errors.trademarkCountries =
          props.labels.formValidation.requiredField;
      }
      setState(currentState);
    };

    const validateTrademarkName = (trademarkName?: string) => {
      const currentState = { ...state };

      if (!trademarkName) {
        trademarkName = props.order!.trademarkName;
      }

      if (trademarkName.length > 0) {
        currentState.errors.trademarkName = undefined;
      } else {
        currentState.errors.trademarkName =
          props.labels.formValidation.requiredField;
      }
      setState(currentState);
    };

    const getSubClasses = (appOrderClass: IAppOrderClass) => {
      return (
        appOrderClass.subClasses
          ?.filter((sc) =>
            sc.description?.text
              ?.toLowerCase()
              .includes(state.classesTextSearchTerm.toLowerCase())
          )
          .map((sc) => sc.description?.text)
          .join(', ') || ''
      );
    };

    const isTextCollapsible = (text: string) => {
      return text.length >= 150;
    };

    const toggleClassCollapse = (appOrderClass: IAppOrderClass) => {
      const currentClasses = [...(classes || [])];
      currentClasses.find((x) => x.id === appOrderClass.id)!.isCollapsed =
        !appOrderClass.isCollapsed;
      setClasses(currentClasses);
    };

    useImperativeHandle(ref, () => ({
      validateStep() {
        validateTrademarkCountries();
        validateTrademarkName();
        return (
          order!.trademarkName.length > 0 &&
          order!.trademarkCountries?.length > 0
        );
      },
    }));

    const setInitialCheckedClasses = useCallback(
      (order: IAppOrder) => {
        const currentState = { ...state };
        currentState.checkedClasses = [...order.trademarkClasses];
        setState(currentState);
      },
      [state]
    );

    useEffect(() => {
      setOrder(props.order);
      setClasses(props.classes);
      setInitialCheckedClasses(props.order);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.classes, props.order]);

    return (
      <div className={OrderProcessDetailsStyle['order-process-details']}>
        {/* Countries selector */}
        <h3>{props.labels.countrySelectionTitle}</h3>
        <div className="left-padded">
          <div
            className={
              OrderProcessDetailsStyle[
                'order-process-details-countries-selector'
              ]
            }>
            <DropDownFilterable
              options={countryOptions}
              placheholder={props.labels.countrySelectionPlaceholder}
              style={{ minWidth: '240px' }}
              error={state.errors.trademarkCountries}
              onChange={(countryId: number) => {
                addCountry(countryId);
              }}
            />
          </div>
        </div>

        {!!props.order.trademarkCountries &&
          props.order.trademarkCountries.length > 0 && (
            <div className="left-padded">
              <ul
                className={
                  OrderProcessDetailsStyle[
                    'order-process-details-countries-selected'
                  ]
                }>
                {props.order.trademarkCountries.map((country, index) => (
                  <li
                    key={`trademarkCountries-${index}`}
                    className={
                      OrderProcessDetailsStyle[
                        'order-process-details-countries-selected-item'
                      ]
                    }>
                    {country.name}
                    <Image
                      quality={90}
                      src={'/assets/images/icons/CloseIcon.svg'}
                      className={OrderProcessDetailsStyle['remove-button']}
                      alt="Remove"
                      width={18}
                      height={18}
                      onClick={() => removeCountry(country.id)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

        {props.showNoServiceAvailableMessage && (
          <div className="left-padded">
            <Message type="subtle">
              <ReactMarkdown>
                {props.labels.noServiceAvailableMessage}
              </ReactMarkdown>
            </Message>
          </div>
        )}
        {/* Countries selector */}

        {/* Trademark information */}
        <h3>{props.labels.trademarkInformationTitle}</h3>
        <div className="left-padded">
          <Input
            type={'text'}
            name={'trademarkName'}
            id={'trademarkName'}
            placeholder={props.labels.trademarkInformationPlaceholder}
            value={props.order.trademarkName}
            error={state.errors.trademarkName}
            onChange={setTrademarkName}
          />

          {props.order.trademarkLogo && props.order.trademarkLogo.length > 0 ? (
            <div>
              <p>{props.labels.uploadLogoTitle}</p>
              <div
                className={
                  OrderProcessDetailsStyle[
                    'order-process-details-logo-container'
                  ]
                }>
                <div
                  className={
                    OrderProcessDetailsStyle[
                      'order-process-details-logo-preview'
                    ]
                  }>
                  <Image
                    quality={90}
                    src={props.order.trademarkLogo}
                    alt="trademark-logo"
                    fill></Image>
                </div>
                <Button color="minimal" onClick={resetTrademarkLogo}>
                  {props.labels.uploadLogoRemoveButton}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p>{props.labels.uploadLogoTip}</p>
              <Input
                type={'file'}
                name={'trademarkLogo'}
                id={'trademarkLogo'}
                onChange={setTrademarkLogo}
              />
            </div>
          )}
        </div>

        <h3>{props.labels.goodsAndServicesTitle}</h3>

        <div className="left-padded">
          <div>
            <ReactMarkdown>
              {props.labels.goodsAndServicesDescription}
            </ReactMarkdown>
          </div>

          <Message
            type="neutral"
            iconPath={'/assets/images/icons/AlertIcon.svg'}>
            <ReactMarkdown>{props.labels.goodsAndServicesNotice}</ReactMarkdown>
          </Message>

          <hr className="separator-thin" />

          <div
            className={
              OrderProcessDetailsStyle['order-process-details-classes-selector']
            }>
            <Button color={'outline'} onClick={() => handleShowClassesModal()}>
              {props.labels.goodsAndServicesButtonLabel}
            </Button>
            <p>{props.labels.goodsAndServicesButtonTip}</p>
          </div>

          {props.order.trademarkClasses &&
          props.order.trademarkClasses.length > 0 ? (
            <div>
              <hr className="separator-thin" />
              <p>
                <strong>{props.labels.goodsAndServicesSelectedTitle}</strong>
              </p>
              <ul
                className={
                  OrderProcessDetailsStyle[
                    'order-process-details-classes-selected'
                  ]
                }>
                {props.order.trademarkClasses
                  .slice()
                  .sort((a, b) => a.code - b.code)
                  .map((x) => x.code)
                  .map((code, index) => {
                    const localizedClass = getClassByCode(props.classes, code);
                    return (
                      !!localizedClass && (
                        <li key={`trademarkClasses-${index}`}>
                          <div
                            className={
                              OrderProcessDetailsStyle[
                                'order-process-details-classes-selected-content-wrapper'
                              ]
                            }>
                            <div
                              className={
                                OrderProcessDetailsStyle[
                                  'order-process-details-classes-selected-content'
                                ]
                              }>
                              <p style={{ marginTop: 0 }}>
                                <strong>Class {localizedClass?.code}</strong>
                              </p>
                              <span>
                                {htmlToText(localizedClass?.name as string)}
                              </span>{' '}
                            </div>

                            <span
                              className={
                                OrderProcessDetailsStyle['remove-button']
                              }
                              onClick={() => handleClassRemove(localizedClass)}>
                              {props.labels.goodsAndServicesRemoveButton}
                            </span>
                          </div>
                          <Input
                            type={'text'}
                            name={'class-' + localizedClass?.code}
                            id={'class-' + localizedClass?.code}
                            value={localizedClass?.description || ''}
                            placeholder={
                              props.labels
                                .goodsAndServicesClassDescripionPlaceholder
                            }
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              setClassDescription(
                                event.target.value,
                                localizedClass?.id
                              )
                            }
                          />
                        </li>
                      )
                    );
                  })}
              </ul>
            </div>
          ) : null}
        </div>

        {/* Trademark information */}

        {/* Classes modal */}
        <Modal show={state.showClassesModal}>
          <ModalHeader>
            <div>
              <h3 className="is-text-align-center">
                {props.labels.goodsAndServicesModalTitle}
              </h3>
              <ReactMarkdown>
                {props.labels.goodsAndServicesModalDescription}
              </ReactMarkdown>
              <div>
                <Input
                  type={'text'}
                  name={''}
                  id={''}
                  placeholder={props.labels.goodsAndServicesSearchPlaceholder}
                  onChange={handleTextSearchChange}
                />
              </div>
            </div>
            <Image
              quality={90}
              src={'/assets/images/icons/CloseIcon.svg'}
              className={ModalStyle['close-icon']}
              alt="Close"
              width={32}
              height={32}
              onClick={handleDismissClassesModal}
            />
          </ModalHeader>
          <ModalContent>
            <ul className="checkboxes-list">
              {filteredClasses().map((item) => (
                <li
                  key={`filteredClasses-${item.id}`}
                  style={{ marginBottom: '20px' }}>
                  <Checkbox
                    id={`class-check-${item.id}`}
                    onChange={(e) => handleClassCheckedChange(e, item)}
                    name={`class-check-${item.id}`}
                    checked={isClassChecked(item)}
                    style={{
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                    }}
                    label={
                      <>
                        {state.classesTextSearchTerm &&
                        state.classesTextSearchTerm.length >= 2 ? (
                          <>
                            <p
                              style={{
                                fontWeight: '400',
                                margin: '-2px 0 0 0',
                              }}>
                              <strong>Class {item.code.toString()}:</strong>{' '}
                              {isTextCollapsible(getSubClasses(item)) ? (
                                <>
                                  {item.isCollapsed
                                    ? `${htmlToText(getSubClasses(item)).slice(
                                        0,
                                        150
                                      )}...`
                                    : htmlToText(getSubClasses(item))}
                                  {item.isCollapsed ? (
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        color: '#307ee1',
                                        fontWeight: 'bold',
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleClassCollapse(item);
                                      }}>
                                      {props.labels.showMore || 'More'}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        color: '#307ee1',
                                        fontWeight: 'bold',
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleClassCollapse(item);
                                      }}>
                                      {props.labels.showLess || 'Less'}
                                    </span>
                                  )}
                                </>
                              ) : (
                                htmlToText(getSubClasses(item))
                              )}
                            </p>
                          </>
                        ) : (
                          <>
                            <p
                              style={{
                                fontWeight: '400',
                                margin: '-2px 0 0 0',
                              }}>
                              <strong>Class {item.code.toString()}:</strong>{' '}
                              {isTextCollapsible(item.name) ? (
                                <>
                                  {item.isCollapsed
                                    ? htmlToText(item.name).slice(0, 150) +
                                      '...'
                                    : htmlToText(item.name)}
                                  {item.isCollapsed ? (
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        color: '#307ee1',
                                        fontWeight: 'bold',
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleClassCollapse(item);
                                      }}>
                                      {props.labels.showMore || 'More'}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        color: '#307ee1',
                                        fontWeight: 'bold',
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleClassCollapse(item);
                                      }}>
                                      {props.labels.showLess || 'Less'}
                                    </span>
                                  )}
                                </>
                              ) : (
                                htmlToText(item.name)
                              )}
                            </p>
                          </>
                        )}
                      </>
                    }></Checkbox>

                  <div style={{ marginLeft: '28px' }}>
                    <Input
                      type={'text'}
                      name={'class-prev-' + item.code}
                      id={'class-prev-' + item.code}
                      value={item.description}
                      disabled={!isClassChecked(item)}
                      placeholder={
                        props.labels.goodsAndServicesClassDescripionPlaceholder
                      }
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setClassDescription(event.target.value, item.id, true)
                      }
                    />
                  </div>
                </li>
              ))}
            </ul>
          </ModalContent>
          <ModalFooter className="buttons-toolbar">
            <Button onClick={() => handleDoneClassesModal()}>
              {props.labels.goodsAndServicesModalDoneButton}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
);

OrderProcessDetails.displayName = 'OrderProcessDetails';
export default OrderProcessDetails;
